import React from 'react'
import { getAvailableSectionTypes, getSectionType, type SectionType, type SongSection } from '../utils/sectionUtils'

interface SectionToolbarProps {
  onInsertSection: (sectionType: SectionType) => void
  onShowSectionNav: () => void
  onToggleSidebar?: () => void
  hasExistingSections: boolean
  showSidebar?: boolean
  currentSection?: string
  hasSelectedText?: boolean
  sections: SongSection[]
}

export const SectionToolbar: React.FC<SectionToolbarProps> = ({
  onInsertSection,
  onShowSectionNav,
  onToggleSidebar,
  hasExistingSections,
  showSidebar = false,
  currentSection,
  hasSelectedText = false,
  sections
}) => {
  const availableSections = getAvailableSectionTypes(sections)
  const getSectionIcon = (sectionType: SectionType) => {
    switch (sectionType) {
      case 'Verse': return '📝'
      case 'Chorus': return '🎵'
      case 'Pre-Chorus': return '✨'
      case 'Bridge': return '🌉'
      case 'Intro': return '🎧'
      case 'Outro': return '🎼'
      case 'Hook': return '🎣'
      default: return '🎶'
    }
  }

  return (
    <div
      data-testid="section-toolbar"
      className="flex flex-wrap items-center gap-2 sm:gap-3 p-2 sm:p-4 bg-gradient-to-r from-slate-800/60 via-slate-900/60 to-slate-800/60 backdrop-blur-xl border-b border-blue-400/20 transition-all duration-300 ease-out shadow-lg"
    >
      <div className="flex items-center space-x-1 sm:space-x-2 animate-in stagger-delay-1">
        <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-400 to-purple-400 animate-pulse"></span>
        <span className="text-xs sm:text-sm font-semibold text-white hidden xs:inline">Quick Insert:</span>
      </div>

      <div className="flex flex-wrap gap-1 sm:gap-2">
        {availableSections.slice(0, 6).map((section, index) => {
          // Smart auto-numbering display: show number only when multiples exist
          const existingOfType = sections.filter(s => {
            const type = getSectionType(s.name)
            return type === section.type
          }).length

          // Determine display name - show numbers intelligently
          let displayName: string = section.type
          if (existingOfType > 0) {
            // For types that can have multiples, show the next number
            if (!['Intro', 'Outro'].includes(section.type)) {
              displayName = `${section.type} ${existingOfType + 1}`
            } else if (existingOfType > 0) {
              // For Intro/Outro, only show number if there's already one
              displayName = `${section.type} ${existingOfType + 1}`
            }
          }

          return (
            <button
              key={section.type}
              data-testid={`section-button-${section.type.toLowerCase().replace(/[^a-z]/g, '-')}`}
              onClick={() => onInsertSection(section.type)}
              className={`group relative overflow-hidden px-2 sm:px-3 py-1.5 sm:py-2 text-xs font-medium text-white bg-white/10 hover:bg-white/20 border border-white/20 hover:border-blue-400/60 rounded-md sm:rounded-lg hover:shadow-lg transition-all duration-200 transform hover:scale-105 backdrop-blur-sm animate-in stagger-delay-${Math.min(index + 2, 5)}`}
              title={hasSelectedText ? `Wrap selected text with ${displayName} section (places tag above selection)` : `Insert ${displayName} section at cursor`}
            >
              <span className="relative z-10 flex items-center space-x-1 transition-all duration-200">
                <span className="transition-transform duration-200 hover:scale-110">{getSectionIcon(section.type)}</span>
                <span className="hidden xs:inline sm:inline">{section.description}</span>
                <span className="xs:hidden sm:hidden">{section.type}</span>
                {existingOfType > 0 && !['Intro', 'Outro'].includes(section.type) && (
                  <span className="text-xs opacity-60 font-normal">
                    {existingOfType + 1}
                  </span>
                )}
                {hasSelectedText && (
                  <span className="text-xs opacity-75 ml-1" title="Will place section tag above selected text">↻</span>
                )}
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
            </button>
          )
        })}
      </div>

      <div className="flex-1" />

      {hasExistingSections && (
        <div className="flex items-center gap-1 sm:gap-2">
          {onToggleSidebar && !showSidebar && (
            <button
              onClick={onToggleSidebar}
              className="group relative overflow-hidden px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-white bg-gradient-to-r from-blue-500/20 to-purple-500/20 hover:from-blue-500/30 hover:to-purple-500/30 border border-blue-400/30 hover:border-blue-400/60 hover:shadow-lg transition-all duration-200 transform hover:scale-105 backdrop-blur-sm rounded-lg sm:rounded-xl"
              title={currentSection ? `Currently in: ${currentSection} - Click to show sidebar` : 'Show section sidebar'}
            >
              <span className="relative z-10 flex items-center space-x-1 sm:space-x-2">
                <span className="group-hover:rotate-12 transition-transform duration-200">📂</span>
                <span className="flex items-center gap-1 sm:gap-2">
                  {currentSection ? (
                    <>
                      <span className="font-medium text-blue-300 hidden sm:inline">{currentSection}</span>
                      <span className="text-gray-400 hidden sm:inline">•</span>
                      <span className="hidden sm:inline">Show Sidebar</span>
                      <span className="sm:hidden">Sidebar</span>
                    </>
                  ) : (
                    <>
                      <span className="hidden sm:inline">Show Sidebar</span>
                      <span className="sm:hidden">Sidebar</span>
                    </>
                  )}
                </span>
              </span>
              <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
            </button>
          )}
          <button
            onClick={onShowSectionNav}
            className="group relative overflow-hidden px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-white bg-gradient-to-r from-blue-500/20 to-purple-500/20 hover:from-blue-500/30 hover:to-purple-500/30 border border-blue-400/30 hover:border-blue-400/60 rounded-lg sm:rounded-xl hover:shadow-lg transition-all duration-200 transform hover:scale-105 backdrop-blur-sm"
            title="Navigate between sections (modal)"
          >
            <span className="relative z-10 flex items-center space-x-1 sm:space-x-2">
              <span className="group-hover:rotate-12 transition-transform duration-200">📋</span>
              <span className="hidden sm:inline">Quick Nav</span>
              <span className="sm:hidden">Nav</span>
            </span>
            <div className="absolute inset-0 bg-gradient-to-r from-blue-400/20 to-purple-400/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200"></div>
          </button>
        </div>
      )}
    </div>
  )
}

export default SectionToolbar
